/**
 * Recovery Service for Data Sync Failures
 * Handles local caching and recovery workflows
 */

import notificationManager from './notificationManager';

class RecoveryService {
  constructor() {
    this.CACHE_PREFIX = 'recovery_cache_';
    this.CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours
  }

  /**
   * Save data to local cache
   */
  saveToCache(key, data) {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        version: 1
      };
      
      localStorage.setItem(
        `${this.CACHE_PREFIX}${key}`,
        JSON.stringify(cacheData)
      );
      
      console.log('💾 Data cached locally:', key);
      return true;
    } catch (error) {
      console.error('❌ Failed to cache data:', error);
      return false;
    }
  }

  /**
   * Load data from local cache
   */
  loadFromCache(key) {
    try {
      const cached = localStorage.getItem(`${this.CACHE_PREFIX}${key}`);
      
      if (!cached) {
        return null;
      }
      
      const cacheData = JSON.parse(cached);
      
      // Check if cache is expired
      if (Date.now() - cacheData?.timestamp > this.CACHE_EXPIRY) {
        console.log('⚠️ Cache expired, removing:', key);
        this.clearCache(key);
        return null;
      }
      
      console.log('✅ Data loaded from cache:', key);
      return cacheData?.data;
    } catch (error) {
      console.error('❌ Failed to load from cache:', error);
      return null;
    }
  }

  /**
   * Clear specific cache entry
   */
  clearCache(key) {
    try {
      localStorage.removeItem(`${this.CACHE_PREFIX}${key}`);
      console.log('🗑️ Cache cleared:', key);
      return true;
    } catch (error) {
      console.error('❌ Failed to clear cache:', error);
      return false;
    }
  }

  /**
   * Get all cached keys
   */
  getCachedKeys() {
    const keys = [];
    
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.CACHE_PREFIX)) {
          keys?.push(key?.replace(this.CACHE_PREFIX, ''));
        }
      }
    } catch (error) {
      console.error('❌ Failed to get cached keys:', error);
    }
    
    return keys;
  }

  /**
   * Check if there are pending sync operations
   */
  hasPendingSync() {
    return this.getCachedKeys()?.length > 0;
  }

  /**
   * Save profile update to cache for recovery
   */
  cacheProfileUpdate(userId, updates) {
    const key = `profile_${userId}`;
    return this.saveToCache(key, {
      userId,
      updates,
      type: 'profile_update',
      failedAt: Date.now()
    });
  }

  /**
   * Get cached profile update
   */
  getCachedProfileUpdate(userId) {
    const key = `profile_${userId}`;
    return this.loadFromCache(key);
  }

  /**
   * Clear cached profile update
   */
  clearProfileCache(userId) {
    const key = `profile_${userId}`;
    return this.clearCache(key);
  }

  /**
   * Attempt to recover failed operation
   */
  async recoverOperation(key, retryFunction) {
    console.log('🔄 Attempting to recover operation:', key);
    
    const cachedData = this.loadFromCache(key);
    
    if (!cachedData) {
      console.log('⚠️ No cached data found for:', key);
      return { success: false, error: 'No cached data' };
    }
    
    try {
      // Attempt to retry the operation
      const result = await retryFunction(cachedData);
      
      if (result?.success) {
        // Clear cache on success
        this.clearCache(key);
        console.log('✅ Operation recovered successfully');
        notificationManager?.success('Data synced successfully!');
        return { success: true, data: result?.data };
      } else {
        console.log('❌ Recovery failed:', result?.error);
        return { success: false, error: result?.error };
      }
    } catch (error) {
      console.error('❌ Recovery exception:', error);
      return { success: false, error };
    }
  }

  /**
   * Show recovery notification to user
   */
  showRecoveryNotification(onRecover) {
    const pendingCount = this.getCachedKeys()?.length;
    
    if (pendingCount > 0) {
      notificationManager?.warning(
        `You have ${pendingCount} pending change${pendingCount > 1 ? 's' : ''} that failed to sync.`,
        {
          duration: 0, // Don't auto-dismiss
          action: {
            label: 'Sync Now',
            onClick: onRecover
          }
        }
      );
    }
  }

  /**
   * Clear all expired cache entries
   */
  clearExpiredCache() {
    const keys = this.getCachedKeys();
    let cleared = 0;
    
    keys?.forEach(key => {
      const cached = this.loadFromCache(key);
      if (!cached) {
        cleared++;
      }
    });
    
    console.log(`🗑️ Cleared ${cleared} expired cache entries`);
    return cleared;
  }
}

const recoveryService = new RecoveryService();
export default recoveryService;